import { useState } from "react";
import api from "../api/api";
import PropTypes from "prop-types";

export const SORT_COLUMNS = [
  { key: "last_name",     label: "Last Name" },
  { key: "first_name",    label: "First Name" },
  { key: "year",          label: "Year" },
  { key: "brand",         label: "Brand" },
  { key: "card_number",   label: "Card #" },
  { key: "grade",         label: "Grade" },
  { key: "card_value",    label: "Card Value" },
  { key: "market_factor", label: "Market Factor" },
  { key: "rookie",        label: "Rookie" },
];

export default function SortModal({ sortConfig, defaultSort, onApply, onClose, showSaveDefault = true, onSaveDefault }) {
  const [levels, setLevels] = useState([...sortConfig]);
  const [setAsDefault, setSetAsDefault] = useState(false);
  const usedKeys = new Set(levels.map(l => l.key));
  const available = SORT_COLUMNS.filter(c => !usedKeys.has(c.key));

  const addLevel = () => {
    if (available.length === 0) return;
    setLevels(prev => [...prev, { key: available[0].key, direction: "asc" }]);
  };
  const removeLevel = (i) => setLevels(prev => prev.filter((_, idx) => idx !== i));
  const updateLevel = (i, field, value) =>
    setLevels(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));

  const handleApply = async () => {
    if (showSaveDefault && setAsDefault) {
      if (onSaveDefault) {
        onSaveDefault(levels);
      } else {
        try { await api.put("/settings/", { default_sort: levels }); } catch (e) {}
      }
    }
    onApply(levels);
    onClose();
  };

  const selStyle = { padding: "0.4rem", borderRadius: 4, border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-primary)" };

  return (
    <div className="modal-overlay" role="button" tabIndex={0} onClick={onClose} onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>  {/* NOSONAR */}
      <div className="modal-box" style={{ width: 640, maxWidth: "95vw" }} onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Advanced Sort</h3>

        {levels.length === 0 && (
          <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>No sort levels. Add one below.</p>
        )}

        {levels.map((level, i) => (
          <div key={`${level.key}-${i}`} style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.5rem" }}>
            <span style={{ color: "var(--text-muted)", fontSize: "0.85rem", width: 20 }}>{i + 1}.</span>
            <select value={level.key} onChange={e => updateLevel(i, "key", e.target.value)} style={{ ...selStyle, flex: 1 }}>
              {SORT_COLUMNS.filter(c => c.key === level.key || !usedKeys.has(c.key)).map(c => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <select value={level.direction} onChange={e => updateLevel(i, "direction", e.target.value)} style={{ ...selStyle, width: 130 }}>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
            <button onClick={() => removeLevel(i)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: "1.1rem" }}
              title="Remove">✕</button>
          </div>
        ))}

        <button onClick={addLevel} disabled={available.length === 0}
          style={{ marginTop: "0.5rem", background: "none", border: "1px dashed var(--border)",
            borderRadius: 6, padding: "0.35rem 0.75rem", cursor: available.length === 0 ? "not-allowed" : "pointer",
            color: "var(--text-muted)", fontSize: "0.85rem", width: "100%" }}>
          + Add Level
        </button>

        {showSaveDefault && (
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem",
            marginTop: "1rem", fontSize: "0.9rem", color: "var(--text-secondary)", cursor: "pointer" }}>  {/* NOSONAR */}
            <input type="checkbox" checked={setAsDefault} onChange={e => setSetAsDefault(e.target.checked)} />
            Set as my default sort order
          </label>
        )}

        <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem", justifyContent: "center" }}>
          <button className="nav-btn" style={{ background: "#c62828" }}
            onClick={() => { onApply([]); onClose(); }}>Clear All</button>
          {showSaveDefault && (
            <button className="nav-btn secondary" disabled={!defaultSort?.length}
              onClick={() => setLevels([...defaultSort])}
              title={defaultSort?.length ? "Restore saved default sort" : "No default sort saved"}>↺ Default</button>
          )}
          <button className="nav-btn secondary" onClick={onClose}>Cancel</button>
          <button className="nav-btn" onClick={handleApply}>Apply</button>
        </div>
      </div>
    </div>
  );
}

SortModal.propTypes = {
  sortConfig: PropTypes.array.isRequired,
  defaultSort: PropTypes.array.isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  showSaveDefault: PropTypes.bool,
  onSaveDefault: PropTypes.func,
};
